import React, { PropsWithChildren, ReactElement, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useIssueRouteParams } from 'hooks/navigation/useRouteParams';
import { useDocuments } from 'store/documents';
import { useIssueStore, usePrefetchIssueChangelog } from 'store/issue';
import { useProject, useProjectId } from 'store/project';
import {
  CurrentIssueForm,
  CurrentIssueFormProvider,
  CurrentIssueMissingReferenceProvider,
  CurrentIssueProvider,
} from './context';
import { useCurrentIssueDocumentQuery, useCurrentIssueProcess } from './hooks';
import { IssueOnSingleView } from './IssueOnSingleView';
import { isRouteProjectSelected, toDocumentsOnIssueView } from './model';
import { useIssueDocumentPreviews } from './useIssueDocumentPreviews';
import { useIssueFields } from './useIssueFields';
import { useIssueOnSingleView } from './useIssueOnSingleView';

/** Opens the issue of the route (or the given one) as the current issue, again in a new language. */
function useOpenCurrentIssue(issueIdFromProps: string | undefined): void {
  const { locale } = useIntl();
  const location = useLocation();
  const { projectId, issueId: issueIdFromRoute } = useIssueRouteParams();
  const stateIssue = location.state?.issue;
  const issueId: string | undefined =
    issueIdFromProps ?? issueIdFromRoute ?? stateIssue?._id ?? stateIssue?.localId;
  const selectedProjectId = useProjectId();
  const openCurrentIssue = useIssueStore.use.openCurrentIssue();

  useEffect(() => {
    if (!issueId || !isRouteProjectSelected(projectId, selectedProjectId)) {
      return;
    }
    return openCurrentIssue(issueId);
  }, [issueId, projectId, selectedProjectId, locale, openCurrentIssue]);

  usePrefetchIssueChangelog(issueId);
}

/** Keeps the documents of the current issue open, with their previews, for `useCurrentIssueDocuments`. */
function useKeepCurrentIssueDocuments(): void {
  const query = useCurrentIssueDocumentQuery();
  const { documents } = useDocuments(query);
  useIssueDocumentPreviews(query?.issueId, documents, toDocumentsOnIssueView(documents));
}

function useCurrentIssueFormFields(issue: IssueOnSingleView | undefined): CurrentIssueForm {
  const { organizationId } = useProject();
  const process = useCurrentIssueProcess();
  const { primaryFields, extendedFields, fieldsLoading } = useIssueFields({
    process,
    siteId: issue?.primaryData.site._id || '',
    organizationId,
  });
  const formReady = Boolean(issue && process && !fieldsLoading && primaryFields.length > 0);
  return useMemo(
    () => ({ primaryFields, extendedFields, formReady }),
    [primaryFields, extendedFields, formReady]
  );
}

/**
 * The current issue of the issue view: opened in the issue store, mapped and its form generated once.
 * Read it with `useCurrentIssue`/`useShownIssue` and `useCurrentIssueForm`; the rest with the
 * `useCurrentIssue*` hooks, which select only what they need.
 */
const WithIssue: React.FC<PropsWithChildren<{ issueId?: string }>> = ({ children, issueId }) => {
  useOpenCurrentIssue(issueId);
  useKeepCurrentIssueDocuments();
  const { issue, missingReference } = useIssueOnSingleView();
  const form = useCurrentIssueFormFields(issue);

  return (
    <CurrentIssueProvider value={issue}>
      <CurrentIssueMissingReferenceProvider value={missingReference}>
        <CurrentIssueFormProvider value={form}>{children}</CurrentIssueFormProvider>
      </CurrentIssueMissingReferenceProvider>
    </CurrentIssueProvider>
  );
};

const withIssue =
  <P extends object>(Component: React.ComponentType<P>) =>
  ({ ...props }: P): ReactElement => (
    <WithIssue>
      <Component {...props} />
    </WithIssue>
  );

export { useCurrentIssue, useCurrentIssueForm, useCurrentIssueMissingReference, useShownIssue } from './context';
export type { CurrentIssueForm } from './context';
export {
  useCurrentIssueAccess,
  useCurrentIssueDocuments,
  useCurrentIssueParams,
  useCurrentIssueProcess,
  useCurrentIssueStages,
  useCurrentIssueWorkflow,
} from './hooks';
export type { CurrentIssueStages } from './hooks';
export { WithIssue, withIssue };
